import { motion } from 'framer-motion';
import { Plus, UserPlus, LayoutGrid, List, Search, FileDown, ChevronDown, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { GLOBAL_TOOLS } from '@/config/tools';


export function DashboardSubnav({
  viewMode,
  onViewModeChange,
  onCreateSpace,
  onInvitePeople,
  onImportExport,
  canCreateSpaces,
  canInviteUsers,
  onGridClick,
  searchQuery,
  onSearchChange
}) {
  const navigate = useNavigate();

  // Global tools configuration (from central config)
  const globalTools = GLOBAL_TOOLS;

  const currentTool = 'spaces';

  return (
    <div className="border-b bg-background">
      <div className="relative flex items-center justify-center gap-6 py-3 px-4 lg:px-6 min-h-[56px]">
        {/* Left: Tool Switcher + Action buttons */}
        <div className="absolute left-6 flex items-center gap-3">
          {/* Global Tools Dropdown */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                size='sm'
                className='h-8 px-2 flex items-center gap-1 border border-gray-200 bg-background hover:bg-muted data-[state=open]:bg-muted data-[state=open]:border-gray-300 rounded-md'
              >
                <LayoutGrid className='h-4 w-4 text-gray-600' />
                <ChevronDown className='h-3 w-3 text-gray-500 data-[state=open]:rotate-180 transition-transform' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='start'
              sideOffset={8}
              className='w-[400px] p-4 rounded-lg border border-gray-200 shadow-lg bg-white'
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <div className='grid grid-cols-3 gap-3'>
                {globalTools.map((tool) => {
                  const Icon = tool.icon;
                  const isActive = tool.id === currentTool;
                  return (
                    <div
                      key={tool.id}
                      className={`relative flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      onClick={(e) => {
                        if (tool.id !== currentTool) {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(tool.route, '_blank');
                          } else {
                            navigate(tool.route);
                          }
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 1 && tool.id !== currentTool) {
                          e.preventDefault();
                          window.open(tool.route, '_blank');
                        }
                      }}
                    >
                      <div
                        className='w-12 h-12 rounded-lg flex items-center justify-center mb-2 flex-shrink-0'
                        style={{ backgroundColor: tool.color + '20' }}
                      >
                        {Icon && (
                          <Icon
                            className='h-6 w-6'
                            style={{ color: tool.color }}
                          />
                        )}
                      </div>
                      <div className='h-8 flex items-center justify-center'>
                        <span className='text-xs font-medium text-center line-clamp-2 w-full leading-tight'>
                          {tool.name}
                        </span>
                      </div>
                      {isActive && (
                        <div className='absolute top-2 right-2'>
                          <CheckCircle className='h-4 w-4 text-blue-600' />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          {canCreateSpaces && (
            <DropdownMenu modal={false}>
              <div className="flex items-center gap-0">
                <Button
                  onClick={onCreateSpace}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors rounded-r-none pr-0"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Space
                </Button>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors rounded-l-none pl-2 pr-1"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent align="end" side="bottom" sideOffset={4} className="w-auto">
                <DropdownMenuItem onClick={onImportExport} className="pr-8 pl-3">
                  <FileDown className="h-4 w-4 mr-2" />
                  Import/Export
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canInviteUsers && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onInvitePeople}
              className="text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite People
            </Button>
          )}
        </div>

        {/* Right: Search bar + View toggle */}
        <div className="absolute right-6 flex items-center gap-3">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search spaces..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 w-64 h-9"
              autoComplete="off"
              spellCheck="false"
            />
          </div>

          {/* View toggle */}
          <div>
            <div className="relative flex items-center p-1 bg-gray-100 rounded-lg">
              {/* Sliding background indicator */}
              <motion.div
                className="absolute h-[32px] bg-white shadow-sm rounded-md"
                initial={false}
                animate={{
                  x: viewMode === 'grid' ? 0 : 74,
                  width: viewMode === 'grid' ? 74 : 66,
                }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 30,
                  mass: 0.8
                }}
              />

              {/* Grid button */}
              <button
                className={`relative flex items-center px-3 py-1.5 transition-colors duration-200 ${
                  viewMode === 'grid'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => {
                  onViewModeChange('grid');
                  onGridClick?.();
                }}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                <span className="text-xs font-medium">Grid</span>
              </button>

              {/* List button */}
              <button
                className={`relative flex items-center px-3 py-1.5 transition-colors duration-200 ${
                  viewMode === 'list'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => onViewModeChange('list')}
              >
                <List className="h-4 w-4 mr-2" />
                <span className="text-xs font-medium">List</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
